///
/// EarthBrowser
///
/// Created by Matt Giger on 9/15/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import { type DatasetFeature } from "@/earthbrowser/dataset-distribution/DatasetLayer"

export type AlertSeverity = "Extreme" | "Severe" | "Moderate" | "Minor" | "Unknown"

export interface AlertProperties extends DatasetFeature {
	id: string
	source: string
	country: string
	event: string
	headline: string | null
	description: string | null
	instruction: string | null
	severity: AlertSeverity
	urgency: string | null
	certainty: string | null
	message_type: string
	category: string | null
	response_type: string | null
	area_desc: string | null
	/** JSON string: { valueName: [values] }, e.g. {"UGC":["TXZ013"]} */
	geocodes: string | null
	sent: string
	effective: string | null
	onset: string | null
	expires: string | null
	ends: string | null
	sender_name: string | null
	web_url: string | null
	centroid_lat: number | null
	centroid_lon: number | null
	bbox_min_lon: number | null
	bbox_min_lat: number | null
	bbox_max_lon: number | null
	bbox_max_lat: number | null
	/** GeoJSON Polygon or MultiPolygon as a JSON string; null for zone-based alerts */
	geometry: string | null
	ingested_at: string
}

const cBatchSize = 200

export class AlertsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists weather_alerts (
					id text primary key,
					source text not null,
					country text not null,
					event text not null,
					headline text,
					description text,
					instruction text,
					severity text not null,
					urgency text,
					certainty text,
					message_type text not null,
					category text,
					response_type text,
					area_desc text,
					geocodes text,
					sent text not null,
					effective text,
					onset text,
					expires text,
					ends text,
					sender_name text,
					web_url text,
					centroid_lat real,
					centroid_lon real,
					bbox_min_lon real,
					bbox_min_lat real,
					bbox_max_lon real,
					bbox_max_lat real,
					geometry text,
					ingested_at text not null
				)`
			},
			{ sql: "create index if not exists idx_weather_alerts_sent on weather_alerts(sent desc)" },
			{ sql: "create index if not exists idx_weather_alerts_severity on weather_alerts(severity)" },
			{ sql: "create index if not exists idx_weather_alerts_country on weather_alerts(country)" },
		])
	}

	public static async replaceAll(rows: AlertProperties[]): Promise<void> {
		await Database.db.exec("delete from weather_alerts", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: AlertProperties[]): Promise<void> {
		const statement = `insert into weather_alerts (
				id, source, country, event,
				headline, description, instruction,
				severity, urgency, certainty,
				message_type, category, response_type,
				area_desc, geocodes,
				sent, effective, onset, expires, ends,
				sender_name, web_url,
				centroid_lat, centroid_lon,
				bbox_min_lon, bbox_min_lat, bbox_max_lon, bbox_max_lat,
				geometry, ingested_at
			) values (
				?, ?, ?, ?,
				?, ?, ?,
				?, ?, ?,
				?, ?, ?,
				?, ?,
				?, ?, ?, ?, ?,
				?, ?,
				?, ?,
				?, ?, ?, ?,
				?, ?
			)
			on conflict(id) do update set
				source = excluded.source,
				country = excluded.country,
				event = excluded.event,
				headline = excluded.headline,
				description = excluded.description,
				instruction = excluded.instruction,
				severity = excluded.severity,
				urgency = excluded.urgency,
				certainty = excluded.certainty,
				message_type = excluded.message_type,
				category = excluded.category,
				response_type = excluded.response_type,
				area_desc = excluded.area_desc,
				geocodes = excluded.geocodes,
				sent = excluded.sent,
				effective = excluded.effective,
				onset = excluded.onset,
				expires = excluded.expires,
				ends = excluded.ends,
				sender_name = excluded.sender_name,
				web_url = excluded.web_url,
				centroid_lat = excluded.centroid_lat,
				centroid_lon = excluded.centroid_lon,
				bbox_min_lon = excluded.bbox_min_lon,
				bbox_min_lat = excluded.bbox_min_lat,
				bbox_max_lon = excluded.bbox_max_lon,
				bbox_max_lat = excluded.bbox_max_lat,
				geometry = excluded.geometry,
				ingested_at = excluded.ingested_at`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(f => [
				f.id,
				f.source,
				f.country,
				f.event,
				f.headline,
				f.description,
				f.instruction,
				f.severity,
				f.urgency,
				f.certainty,
				f.message_type,
				f.category,
				f.response_type,
				f.area_desc,
				f.geocodes,
				f.sent,
				f.effective,
				f.onset,
				f.expires,
				f.ends,
				f.sender_name,
				f.web_url,
				f.centroid_lat,
				f.centroid_lon,
				f.bbox_min_lon,
				f.bbox_min_lat,
				f.bbox_max_lon,
				f.bbox_max_lat,
				f.geometry,
				f.ingested_at,
			]))
		}
	}

	public static async deleteByIds(ids: string[]): Promise<void> {
		if (ids.length === 0) return
		const placeholders = ids.map(() => "?").join(",")
		await Database.db.exec(`delete from weather_alerts where id in (${placeholders})`, ids, "critical")
	}

	public static async getById(id: string): Promise<AlertProperties | null> {
		const rows = await Database.db.exec(
			`select * from weather_alerts where id = ?`,
			[id],
			"low"
		) as AlertProperties[]
		return rows[0] ?? null
	}

	/** All alert ids currently in the table (for new-alert diffing on sync) */
	public static async getAllIds(): Promise<Set<string>> {
		const rows = await Database.db.exec(
			`select id from weather_alerts`,
			[],
			"low"
		) as Array<{ id: string }>
		return new Set(rows.map(r => String(r.id)))
	}

	/** All alerts, most severe first, then most recently sent */
	public static async getAll(): Promise<AlertProperties[]> {
		const rows = await Database.db.exec(
			`select * from weather_alerts
			order by
				case severity
					when 'Extreme' then 0
					when 'Severe' then 1
					when 'Moderate' then 2
					when 'Minor' then 3
					else 4
				end,
				sent desc`,
			[],
			"low"
		) as AlertProperties[]
		return rows
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from weather_alerts`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}
}
